/* eslint-disable react/jsx-props-no-spreading */
import { ReactElement } from "react";
import { Stack, Typography, TextField, TextFieldProps } from "@mui/material";
import {
  FieldPath,
  FieldValues,
  useController,
  UseControllerProps,
} from "react-hook-form";

type Z1fmLabelInputProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = UseControllerProps<TFieldValues, TName> & {
  label?: string;
  /** Additional settings */
  config?: {
    /**
     * Whether to display error messages below the input field.
     * If `true`, message displays.
     * @defaultValue `false`
     */
    displayErrorMessage?: boolean;
  };
  /** Settings for MUI elements */
  muiProps?: {
    /**
     * Settings for TextField inside Z1fmTextField
     *
     * API: {@link https://mui.com/material-ui/api/text-field/}
     */
    textFieldProps?: TextFieldProps;
  };
};

/**
 * MUI TextField component linked to React Hook Form.
 * Type arguments are optional, but specifying them provides powerful type checking and type inference.
 * @typeParam TFieldValues - Type of the form.
 * @typeParam TName - Field name.
 * @example
 * ```
 * type FormData = {
 *   username: string;
 * };
 *
 * <Z1fmLabelInput<FormData, 'username'>
 *   name='username'
 *   control={control}
 *   rules={{
 *     required: 'Enter your username.',
 *     maxLength: {
 *       value: 10,
 *       message: 'Enter up to 10 characters.'
 *     }
 *   }}
 *   config={{
 *     displayErrorMessage: true
 *   }}
 *   muiProps={{
 *     textFieldProps: {
 *       label: 'Username',
 *       fullWidth: true
 *     }
 *   }}
 * />
 * ```
 */
export const Z1fmLabelInput: <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: Z1fmLabelInputProps<TFieldValues, TName>,
) => ReactElement = (props) => {
  const { muiProps, config, ...others } = props;
  const { textFieldProps } = muiProps ?? {};
  const { field, fieldState } = useController(others);

  // <FormControl variant="standard">
  //   <InputLabel htmlFor="component-helper">Name</InputLabel>
  //   <Input
  //     id="component-helper"
  //     name={field.name}
  //     value={field.value}
  //     inputRef={field.ref}
  //     defaultValue="Composed TextField"
  //     aria-describedby="component-helper-text"
  //     onChange={field.onChange}
  //     onBlur={field.onBlur}
  //     sx={textFieldProps}
  //   />
  //   {!!fieldState.error ?
  //     <FormHelperText id="component-helper-text">
  //       {config?.displayErrorMessage && fieldState.error?.message}
  //     </FormHelperText>
  //     : <></>
  //   }
  // </FormControl>

  return (
    <Stack direction="column">
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        {props.label}
      </Typography>
      <TextField
        // Attention to ther order of parameters passing
        {...textFieldProps}
        inputRef={field.ref}
        id={field.name}
        name={field.name}
        value={field.value}
        error={!!fieldState.error}
        helperText={config?.displayErrorMessage && fieldState.error?.message}
        onChange={field.onChange}
        onBlur={field.onBlur}
      />
    </Stack>
  );
};
